/**
 * Anmeldung an Entra ID.
 *
 * Umleitung statt Aufklappfenster: Aufklappfenster werden von Browsern und
 * Gruppenrichtlinien gerne blockiert, und in einem eingebetteten Rahmen
 * (Teams, Intranetportal) funktionieren sie oft gar nicht.
 *
 * Die Anmeldung gilt fuer **alle Fenster** des Browsers und laeuft nach
 * acht Stunden ab — ein Arbeitstag. Bis zum 06.10.2026 lag sie im
 * Sitzungsspeicher des einzelnen Reiters: jedes neue Fenster verlangte eine
 * eigene Anmeldung (gemeldet im produktiven Test der HOWOGE, Punkt 17). Die
 * Frist ersetzt den Schutz, den der Reiterspeicher auf geteilten Rechnern
 * bot: wer sich morgens anmeldet, ist am naechsten Tag wieder abgemeldet,
 * und „Abmelden" raeumt sofort.
 */

import {
  PublicClientApplication,
  type Configuration,
  type SilentRequest,
} from '@azure/msal-browser';
import { OHNE_ANMELDUNG } from './entwicklung.js';

const anwendungId = import.meta.env['VITE_ENTRA_ANWENDUNG_ID'] as string | undefined;
const tenantId = import.meta.env['VITE_ENTRA_TENANT_ID'] as string | undefined;

// Im Entwicklungsmodus gibt es keine Anmeldung und damit auch keine
// Registrierung. Ausserhalb davon ist eine fehlende Angabe ein Baufehler und
// muss laut werden — eine Anwendung ohne Anmeldung darf nicht ausgeliefert
// werden.
if (!OHNE_ANMELDUNG && (!anwendungId || !tenantId)) {
  throw new Error(
    'VITE_ENTRA_ANWENDUNG_ID und VITE_ENTRA_TENANT_ID müssen beim Bau gesetzt sein.',
  );
}

const einstellungen: Configuration = {
  auth: {
    clientId: anwendungId ?? '00000000-0000-0000-0000-000000000000',
    authority: `https://login.microsoftonline.com/${tenantId ?? 'common'}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      // Kein eigenes Protokoll: die Bibliothek wuerde Kennungen mitschreiben
      loggerCallback: () => undefined,
      piiLoggingEnabled: false,
    },
  },
};

export const msal = new PublicClientApplication(einstellungen);

/* ---------- Sitzungsfrist: acht Stunden */

const SITZUNG_MS = 8 * 60 * 60 * 1000;
const SITZUNG_SCHLUESSEL = 'iammatrix.anmeldungAm';

/** Merkt den Zeitpunkt der Anmeldung — fuer alle Fenster gemeinsam. */
export function sitzungMerken(): void {
  try { localStorage.setItem(SITZUNG_SCHLUESSEL, String(Date.now())); } catch { /* ohne Speicher */ }
}

/** Ist die Anmeldung aelter als acht Stunden — oder ohne Zeitstempel? */
export function sitzungAbgelaufen(): boolean {
  try {
    const am = Number(localStorage.getItem(SITZUNG_SCHLUESSEL) ?? 0);
    return !am || Date.now() - am > SITZUNG_MS;
  } catch {
    return true;
  }
}

/**
 * Raeumt eine abgelaufene Anmeldung aus dem Speicher. Danach zeigt die
 * Anwendung den Anmeldeschirm; ein Klick genuegt, solange Entra die Person
 * noch kennt.
 */
export async function sitzungBeenden(): Promise<void> {
  try { localStorage.removeItem(SITZUNG_SCHLUESSEL); } catch { /* ohne Speicher */ }
  await msal.clearCache();
}

/** Der Bereich, den die Oberflaeche fuer den Zugriff auf die API anfordert. */
export const bereich = `api://${anwendungId}/access_as_user`;

/**
 * Meldet ab — bei Entra **und** im Browser.
 *
 * Bis zum 11.08.2026 gab es das nicht. Der Eintrag „Abmelden" im Konto-Menue
 * war ein Verweis auf `/anmelden`, eine Route, die es nie gab: der Nutzer
 * landete auf „Nicht gefunden" und blieb angemeldet. Auf einem geteilten
 * Rechner ist das der Fehler, der am teuersten wird.
 *
 * `logoutRedirect` raeumt den Sitzungsspeicher und beendet die Sitzung auch
 * beim Anmeldedienst. Nur den lokalen Speicher zu leeren wuerde nicht reichen —
 * die naechste Anmeldung liefe stillschweigend durch, weil Entra die Sitzung
 * noch fuehrt.
 */
export async function abmelden(): Promise<void> {
  if (OHNE_ANMELDUNG) {
    // Im Entwicklungsmodus gibt es keine Anmeldung, die man beenden koennte.
    // Statt stillschweigend nichts zu tun, wird wenigstens der gemerkte
    // Zustand geraeumt und neu geladen — sichtbar und nachvollziehbar.
    try { sessionStorage.clear(); } catch { /* kein Speicher verfuegbar */ }
    window.location.assign('/');
    return;
  }
  const konto = msal.getAllAccounts()[0];
  try { localStorage.removeItem(SITZUNG_SCHLUESSEL); } catch { /* ohne Speicher */ }
  await msal.logoutRedirect(konto ? { account: konto } : {});
}

/**
 * Holt ein Zugriffstoken. Erst still aus dem Zwischenspeicher; erst wenn das
 * nicht geht, wird der Nutzer umgeleitet. So sieht niemand bei jedem
 * Seitenwechsel einen Anmeldebildschirm.
 */
export async function tokenHolen(): Promise<string> {
  // Entwicklungsmodus: der lokale Datenserver kennt keine Anmeldung.
  if (OHNE_ANMELDUNG) return '';
  /* Nach acht Stunden ist Schluss — auch mitten in der Arbeit. Die Seite
     laedt neu und zeigt den Anmeldeschirm. */
  if (msal.getAllAccounts().length > 0 && sitzungAbgelaufen()) {
    await sitzungBeenden();
    window.location.reload();
    throw new Error('Die Anmeldung ist abgelaufen.');
  }
  const konten = msal.getAllAccounts();
  const konto = konten[0];
  if (!konto) throw new Error('Nicht angemeldet.');

  const anfrage: SilentRequest = { scopes: [bereich], account: konto };
  try {
    const ergebnis = await msal.acquireTokenSilent(anfrage);
    return ergebnis.accessToken;
  } catch {
    await msal.acquireTokenRedirect({ scopes: [bereich], account: konto });
    // acquireTokenRedirect verlaesst die Seite; die Zeile ist nur fuer den Typ
    throw new Error('Anmeldung wird erneuert.');
  }
}
